import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  BriefcaseIcon,
  CalendarIcon,
  CalendarPlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LayoutGridIcon,
  PlusIcon,
} from 'lucide-react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { parseAsStringLiteral, useQueryState } from 'nuqs';

import {
  agendaRange,
  getCalendarMatrix,
  gridRange,
  startOfMonth,
} from '@/components/calendar/helpers';
import type { CalendarView } from '@/components/calendar/types';
import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { ViewToggle } from '@/components/ui/view-toggle';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { useEntitlements } from '@/hooks/use-entitlements';
import { createCustomer } from '@/lib/api/customers';
import {
  businessVisitsQueryKey,
  createJob,
  jobsQueryKey,
  listBusinessVisits,
  listJobs,
  type ListJobsFilters,
} from '@/lib/api/jobs';

import { AddJobModal } from './add-job-modal';
import { FilterBar } from './filter-bar';
import {
  assignmentState,
  filterBusinessVisits,
  getMonthRange,
  getWeekRange,
  isRecurring,
  PAGE_SIZE,
} from './helpers';
import { JobsCalendar } from './jobs-calendar';
import { JobsGrid } from './jobs-grid';
import type {
  AssignmentFilter,
  DateFilter,
  FilterState,
  Job,
  NewJobSubmission,
  TypeFilter,
} from './types';

const VIEW_PARAM = parseAsStringLiteral(['list', 'calendar'] as const).withDefault(
  'list',
);

const DEFAULT_FILTERS: FilterState = {
  date: 'all',
  type: 'all',
  assignment: 'all',
};

export function Jobs() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const { has } = useEntitlements();

  // 'new' opens a blank Add Job form; a `Job` opens it prefilled for "Copy Job".
  const [addModal, setAddModal] = useState<'new' | Job | null>(null);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [view, setView] = useQueryState('view', VIEW_PARAM);
  const [calDate, setCalDate] = useState(() => startOfMonth(new Date()));
  const [calView, setCalView] = useState<CalendarView>('month');

  // ── Data ──────────────────────────────────────────────────────────────────────

  // List and calendar view now fetch independently — the list is jobs
  // filtered by the date preset, the calendar is real visits over the visible
  // span (the 6×7 grid for month, a rolling window for agenda), so a visit
  // that was individually rescheduled or skipped plots on its real date
  // instead of a recomputed one.
  const calRange = useMemo(
    () =>
      calView === 'agenda'
        ? agendaRange(calDate, 30)
        : gridRange(getCalendarMatrix(startOfMonth(calDate))),
    [calDate, calView],
  );

  const serverFilters = useMemo<ListJobsFilters>(() => {
    const f: ListJobsFilters = {};

    if (filters.date === 'week') {
      const { start, end } = getWeekRange();
      f.start_date_from = start;
      f.start_date_to = end;
    } else if (filters.date === 'month') {
      const { start, end } = getMonthRange();
      f.start_date_from = start;
      f.start_date_to = end;
    }

    return f;
  }, [filters.date]);

  const jobsQuery = useQuery({
    queryKey: jobsQueryKey(businessId ?? '', serverFilters),
    queryFn: () => listJobs(businessId!, serverFilters),
    enabled: Boolean(businessId),
    placeholderData: keepPreviousData,
  });

  const visitsQuery = useQuery({
    queryKey: businessVisitsQueryKey(businessId ?? '', {
      scheduled_date_from: calRange.start,
      scheduled_date_to: calRange.end,
    }),
    queryFn: () =>
      listBusinessVisits(businessId!, {
        scheduled_date_from: calRange.start,
        scheduled_date_to: calRange.end,
      }),
    enabled: Boolean(businessId) && view === 'calendar',
    placeholderData: keepPreviousData,
  });

  const createMutation = useMutation({
    mutationFn: async (submission: NewJobSubmission) => {
      let customerUserId = submission.customerUserId;

      // "New customer…" path: create the customer first, then use the
      // user id the API links to it for the job.
      if (submission.mode === 'new') {
        const created = await createCustomer(businessId!, {
          full_name: submission.newCustomerName!,
          email: submission.newCustomerEmail || undefined,
          phone: submission.newCustomerPhone || undefined,
        });
        if (!created.customer_user_id) throw new Error(t.jobCustomerNoAccount);
        customerUserId = created.customer_user_id;
      }

      return createJob(businessId!, {
        ...submission.job,
        customer_user_id: customerUserId!,
      });
    },
    meta: { successMessage: t.jobAdded },
    onSuccess: (created) => {
      setAddModal(null);
      // Every job — one-time or recurring — has a detail page with its
      // visits timeline; land the user there.
      navigate(`/jobs/${created.id}`);
    },
  });

  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);

  // `type` and `assignment` have no server query params, so they're applied
  // client-side over whatever page the server already returned.
  const filtered = useMemo(() => {
    let list = jobs;

    if (filters.type !== 'all') {
      list = list.filter((j) => (filters.type === 'recurring' ? isRecurring(j) : !isRecurring(j)));
    }

    if (filters.assignment !== 'all') {
      // Jobs whose count the API hasn't sent yet ('unknown') never match a
      // specific assignment filter.
      list = list.filter((j) => assignmentState(j) === filters.assignment);
    }

    return [...list].sort((a, b) => a.start_date.localeCompare(b.start_date));
  }, [jobs, filters.type, filters.assignment]);

  const filteredVisits = useMemo(
    () => filterBusinessVisits(visitsQuery.data ?? [], filters),
    [visitsQuery.data, filters],
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasFilters =
    filters.date !== 'all' ||
    filters.type !== 'all' ||
    filters.assignment !== 'all';

  // ── Handlers ─────────────────────────────────────────────────────────────────

  function handleDateChange(date: DateFilter) {
    setFilters((f) => ({ ...f, date }));
    setPage(1);
  }

  function handleTypeChange(type: TypeFilter) {
    setFilters((f) => ({ ...f, type }));
    setPage(1);
  }

  function handleAssignmentChange(assignment: AssignmentFilter) {
    setFilters((f) => ({ ...f, assignment }));
    setPage(1);
  }

  function handleClearFilters() {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <BriefcaseIcon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold leading-tight">{t.jobs}</h1>
            <p className="text-xs text-muted-foreground">
              {jobs.length} {t.jobsCount}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ViewToggle
            value={view}
            onChange={(next) => void setView(next)}
            aria-label={t.jobs}
            options={[
              {
                value: 'list',
                label: t.listView,
                icon: LayoutGridIcon,
                testId: 'view-toggle-list',
              },
              {
                value: 'calendar',
                label: t.calendarView,
                icon: CalendarIcon,
                testId: 'view-toggle-calendar',
              },
            ]}
          />
          {view === 'calendar' && (
            <Button
              asChild
              size="sm"
              variant="outline"
              className="gap-1.5"
              data-testid="calendar-subscribe"
            >
              <Link to="/business-page">
                <CalendarPlusIcon className="w-4 h-4" />
                {t.calendarSubscribe}
              </Link>
            </Button>
          )}
          <Button
            size="sm"
            className="gap-1.5"
            data-testid="add-job"
            onClick={() => setAddModal('new')}
          >
            <PlusIcon className="w-4 h-4" />
            {t.addJob}
          </Button>
        </div>
      </div>

      <FilterBar
        filters={filters}
        totalCount={filtered.length}
        onDateChange={handleDateChange}
        onTypeChange={handleTypeChange}
        onAssignmentChange={handleAssignmentChange}
        onClear={handleClearFilters}
        showDateFilter={view === 'list'}
        showAssignmentFilter={has('employees')}
      />

      {view === 'calendar' ? (
        visitsQuery.isPending ? (
          <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
        ) : (
          <JobsCalendar
            visits={filteredVisits}
            businessId={businessId!}
            date={calDate}
            view={calView}
            onNavigate={setCalDate}
            onView={setCalView}
            onSelectJob={(jobId) => navigate(`/jobs/${jobId}`)}
            isLoading={visitsQuery.isFetching}
          />
        )
      ) : jobsQuery.isPending ? (
        <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>
      ) : (
        <JobsGrid
          jobs={paginated}
          hasFilters={hasFilters}
          onClearFilters={handleClearFilters}
          onSelect={(jobId) => navigate(`/jobs/${jobId}`)}
          onCopy={(job) => setAddModal(job)}
        />
      )}

      {addModal && businessId && (
        <AddJobModal
          businessId={businessId}
          onClose={() => setAddModal(null)}
          onSave={(submission) => createMutation.mutate(submission)}
          isSaving={createMutation.isPending}
          sourceJob={addModal === 'new' ? undefined : addModal}
        />
      )}

      {view === 'list' && totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="gap-1.5"
            data-testid="prev-page"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            {t.previous}
          </Button>

          <span className="text-xs text-muted-foreground">
            {page} {t.pageOf} {totalPages}
          </span>

          <Button
            variant="outline"
            size="sm"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="gap-1.5"
            data-testid="next-page"
          >
            {t.nextPage}
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
